import type { Teaser } from '@/types/Teaser';
import { fetchConfig, fetchTeaser } from '@/lib/api/moin-api';
import type { Botconfig } from '@/types';
import logger from '@/logger';
import objectPath from 'object-path';


// legacy config interface
// just don't look at it
interface OldConfig {
  [key: string]: string | number | boolean | object | null;
}

export const v2tov3 = (oldconfig: OldConfig) => {
  const newconfig = {};
  let themeglobalborder = {
    borderradius: {
      outer: '',
      inner: '',
      shape: '',
    },
  };
  if (oldconfig.theme === 'square') {
    themeglobalborder = {
      borderradius: {
        outer: '0px',
        inner: '6px',
        shape: '0px',
      },
    };
  }
  const components = {
    widget: {
      global: {
        ...themeglobalborder,
        position: {
          bottom: '',
          right: '',
          animationEnabled: false,
        },
      },
      header: {
        background: objectPath.get(oldconfig, 'header.background', 'red'),
        textcolor: objectPath.get(oldconfig, 'header.textcolor', 'white'),
        name: objectPath.get(oldconfig, 'header.name'),
      },
      operator: {
        default: {
          name: objectPath.get(oldconfig, 'liveChat.defaultOperatorName'),
          titel: objectPath.get(oldconfig, 'liveChat.defaultOperatorTitle'),
          avatar_url: objectPath.get(
            oldconfig,
            'liveChat.defaultOperatorImage',
          ),
        },
      },
      conversation: {background: objectPath.get(oldconfig, 'msg.container.background'),},
      widget_button: {
        background: objectPath.get(oldconfig, 'floatbutton.background'),
        textcolor: '',
      },
      input: {placeholder: objectPath.get(oldconfig, 'inputPlaceholder'),},
    },
    messageTypes: {
      textMessage: {
        background: objectPath.get(oldconfig, 'msg.bot.background'),
        textcolor: objectPath.get(oldconfig, 'msg.bot.textcolor'),
      },
      userMessage: {
        background: objectPath.get(oldconfig, 'msg.user.background'),
        textcolor: objectPath.get(oldconfig, 'msg.user.textcolor'),
      },
      qr: {
        background: objectPath.get(oldconfig, 'msg.qr.background'),
        background_hover: objectPath.get(oldconfig, 'msg.qr.background_hover'),
        textcolor: objectPath.get(oldconfig, 'msg.qr.textcolor'),
        bordercolor: objectPath.get(oldconfig, 'msg.qr.border'),
        bordercolor_hover: objectPath.get(oldconfig, 'msg.qr.border_hover'),
        textcolor_hover: objectPath.get(oldconfig, 'msg.qr.textcolor_hover'),
      },
      card: {
        background: objectPath.get(oldconfig, 'msg.card.background'),
        button: {
          background: objectPath.get(oldconfig, 'msg.card.button.background'),
          textcolor: objectPath.get(oldconfig, 'msg.card.button.textcolor'),
          border: objectPath.get(oldconfig, 'msg.card.button.border'),
        },
      },
      carousel: {background: objectPath.get(oldconfig, 'msg.card.button_carousel'),},
    },
  };

  return {
    ...newconfig,
    version: '4',
    active: oldconfig.active,
    behaviour: {widgetOpen: objectPath.get(oldconfig, 'widgetOpen', 'restorePrevious'),},
    channelId: oldconfig.channelId,
    useLocalStorage: !oldconfig.forceReload,
    poweredBy: oldconfig.poweredBy,
    teaser: oldconfig.teaser,
    components,
  };
};




/**
 * gets a bot config for the given botId and channelId
 * It will also check if there is a config embedded in the parent window
 * On development mode it will also check of there a config overwrite via a url query parameter
 * e.g. localhost:8080/?config=default
 * will load the config from /configs/default.jsonc
 *
 * If there is an error while fetching the config it will throw an error
 *
 * @param botId
 * @param channelId
 * @returns Botconfig
 */

export const getConfig = async (botId: string, channelId?: string): Promise<Botconfig> => {
  if (!botId) throw new Error('No BotId to fetch config');
  // There might be a config embedded in the parent window
  // If so it get the higest priority

  // @ts-expect-error we don't really know if the config is there or not
  const embeddedConfig = window.parent?.moin?.config;
  let configData = embeddedConfig ? embeddedConfig : await fetchConfig(botId, channelId);

  if (parseInt(configData.version) < 3) {
    configData = v2tov3(configData);
    console.warn('converted config from v2 to v3');
    /* throw new Error('Config version is too old'); */
  }

  // we need to support the legacy config entry to force a reload for all users
  // therefore we disable the localstorage usage for the time beeing and everyone get a new session
  if (configData.forceReload) {
    configData.useLocalStorage = false;
  }

  // there can be the case that no channelId is given with the moin-loader initialization
  // in this case we use the channel id given by the config
  // there also COULD be the case that the channelID given does not match the channel from the config
  // in this case we assume the config channelId is the most recent one and we use that
  if (channelId && channelId !== configData.channelId) {
    logger.warn('channel id missmatch / missing, using the config channelid');
  }

  return {
    botId,
    ...configData,
  };
}

/**
 * check if there is a teaser for the given botId and channelId and isMobile flag
 * if there is no teaser available it will return undefined
 * if there is an error while fetching the teaser it will return undefined
 * @param botId
 * @param channelId
 * @param isMobile
 * @returns Teaser | undefined
 */
export const getTeaser = async (botId: string, channelId: string, isMobile: boolean): Promise<Teaser | undefined> => {
  try {
    const location = window.parent.location;
    const teaserResponse = await fetchTeaser(botId, channelId, isMobile, `${location.hostname}${location.pathname}`);
    return {
      ...teaserResponse,
      forceShow: false,
      qr: teaserResponse.quickActions.map((intent) => {
        return {
          text: intent.text,
          intent: {
            name: intent.intent,
            parameters: {confidence: 1,}
          },

        }
      }),
    }

  } catch {
    // we don't want to break the whole widget if the teaser is not available of there is an error
    return
  }
}
